import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MediaOverview, MediaRun, MediaRunDetail, MediaRunKind, MediaSource, MediaSourceInput } from "../../shared/media";
import { isFinished } from "../../shared/media";
import { api } from "./api";

// Photo library analysis (admin only). Polls quickly while a run is active
// so progress feels live, and slowly otherwise.

const OVERVIEW = ["media", "overview"] as const;

export function useMediaOverview() {
  return useQuery({
    queryKey: OVERVIEW,
    queryFn: () => api<MediaOverview>("/media/overview"),
    refetchInterval: (q) => {
      const runs = q.state.data?.runs ?? [];
      return runs.some((r) => !isFinished(r.status)) ? 4000 : 30_000;
    },
  });
}

export function useMediaRun(id: string | null, live: boolean) {
  return useQuery({
    queryKey: ["media", "run", id],
    queryFn: () => api<MediaRunDetail>(`/media/runs/${id}`),
    enabled: !!id,
    refetchInterval: live ? 4000 : false,
  });
}

export function useMediaActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["media"] });
  return {
    pairing: useMutation({ mutationFn: () => api<{ code: string; expiresAt: string }>("/media/pairing", { method: "POST", body: {} }) }),
    disconnect: useMutation({ mutationFn: (id: string) => api(`/media/agents/${id}`, { method: "DELETE" }), onSuccess: refresh }),
    saveSource: useMutation({
      mutationFn: ({ id, input }: { id: string | null; input: MediaSourceInput }) =>
        id ? api<MediaSource>(`/media/sources/${id}`, { method: "PATCH", body: input }) : api<MediaSource>("/media/sources", { method: "POST", body: input }),
      onSuccess: refresh,
    }),
    start: useMutation({
      mutationFn: ({ sourceId, kind }: { sourceId: string; kind: MediaRunKind }) => api<MediaRun>("/media/runs", { method: "POST", body: { sourceId, kind } }),
      onSuccess: refresh,
    }),
    control: useMutation({
      mutationFn: ({ id, action }: { id: string; action: "pause" | "resume" | "cancel" }) => api<MediaRun>(`/media/runs/${id}/control`, { method: "POST", body: { action } }),
      onSuccess: refresh,
    }),
  };
}
